'use client';

/**
 * Partner-scoped IAM page (mounted at `/partner-admin/:partner/team`).
 *
 * Shape mirrors the global `/admin/iam` panel: two tabs, Staff and
 * Merchants. Both surfaces are scope-clamped to the active partner
 * by the backend — a partneradmin or scoped AM only ever sees the
 * users + merchants attached to their own partner, regardless of
 * any filter they could craft client-side. See:
 *   - `useCases/iam/queries/listUsers.ts::resolveEffectivePartnerScope`
 *   - `useCases/iam/orgs/listOrgs.ts::Scope auto-clamp` block
 *
 * Why this page exists alongside `/admin/iam`
 * -------------------------------------------
 * `/admin/iam` is gated to superadmin / payment_ops / internal AM by
 * the AdminLayout chrome — partneradmins can't reach it. They land
 * here instead, with the same IAM affordances cropped to their own
 * partner. Same components (`OrgsTab`, the partners.* mutations),
 * different mount point, different navigation chrome.
 *
 * Access control
 * --------------
 *   - superadmin: can view any partner via the URL `:partner` param.
 *   - partneradmin: only their own `whitelabelOwnership`. URL
 *     mismatch → "Access Denied" via `usePartnerAdmin`.
 *   - everyone else: denied.
 *
 * The Staff tab keeps the legacy invite-by-email and role checkbox
 * affordances (the partner-only mutations: `setTeamMemberRoles`,
 * `inviteTeamMember`). The Merchants tab is a thin wrapper around
 * the shared `<OrgsTab />` — it auto-hides the partner picker when
 * the caller is partner-scoped (one filter, one option, no point).
 */
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
    AlertCircle,
    Building2,
    Loader2,
    Mail,
    ShieldAlert,
    UserPlus,
    Users,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { OrgsTab } from '@/components/iam/OrgsTab';
import { usePartnerAdmin } from '@/hooks/use-partner-admin';
import { trpc } from '@/lib/api/trpc';

export default function PartnerAdminTeamPage() {
    const { partner: partnerParam } = useParams<{ partner: string }>();
    const { canView, partner, isSuperAdmin, isLoading: loadingRole } =
        usePartnerAdmin(partnerParam);

    if (loadingRole) {
        return (
            <div className="p-6 text-sm text-muted-foreground">Loading...</div>
        );
    }

    if (!canView || !partner) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="text-center">
                    <ShieldAlert className="mx-auto h-12 w-12 text-muted-foreground" />
                    <h2 className="mt-4 text-xl font-semibold">Access Denied</h2>
                    <p className="mt-2 max-w-md text-sm text-muted-foreground">
                        This IAM page is restricted to partner admins for{' '}
                        <strong>{partnerParam ?? '(unset)'}</strong>.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <header className="space-y-1.5 border-b pb-4">
                <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold tracking-tight">
                        Identity &amp; access
                    </h2>
                    <Badge variant="secondary" className="text-xs uppercase">
                        {partner}
                    </Badge>
                    {isSuperAdmin && (
                        <Badge variant="outline" className="text-xs">
                            superadmin
                        </Badge>
                    )}
                </div>
                <p className="max-w-3xl text-sm text-muted-foreground">
                    Manage the staff scoped to this partner and the membership
                    of every merchant org attached to it. Cross-partner data
                    is intentionally hidden — every list here is scope-clamped
                    server-side.
                </p>
            </header>

            <Tabs defaultValue="staff" className="space-y-6">
                <TabsList>
                    <TabsTrigger value="staff" className="gap-2">
                        <Users className="h-4 w-4" />
                        Staff
                    </TabsTrigger>
                    <TabsTrigger value="merchants" className="gap-2">
                        <Building2 className="h-4 w-4" />
                        Merchants
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="staff" className="space-y-6">
                    <StaffTab partner={partner} />
                </TabsContent>

                <TabsContent value="merchants" className="space-y-6">
                    <OrgsTab />
                </TabsContent>
            </Tabs>
        </div>
    );
}

/**
 * Staff sub-tab: lists the users currently scoped to this partner
 * and lets partneradmins flip their assignable roles or invite new
 * teammates by email. Same component as before the IAM unification
 * — only the surrounding chrome changed.
 */
function StaffTab({ partner }: { partner: string }) {
    const listQuery = trpc.partners.listTeam.useQuery(
        { partner },
        { staleTime: 30_000 },
    );
    const assignableQuery = trpc.partners.listAssignableTeamRoles.useQuery(
        { partner },
        { staleTime: 60 * 60 * 1000 },
    );

    const utils = trpc.useUtils();
    const setRolesMutation = trpc.partners.setTeamMemberRoles.useMutation({
        onSuccess: async () => {
            await utils.partners.listTeam.invalidate();
        },
    });

    const assignableRoles = assignableQuery.data?.assignableRoles ?? [];

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <Alert className="flex-1">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>How partner-admin delegation works</AlertTitle>
                    <AlertDescription className="text-sm">
                        Target users must already be scoped to{' '}
                        <span className="font-mono">{partner}</span>. To bring
                        a brand-new email INTO this partner, use{' '}
                        <strong>Invite member</strong>; existing Tagada users
                        attached elsewhere can only be re-attached by a Tagada
                        superadmin.
                    </AlertDescription>
                </Alert>
                <InviteTeamMemberDialog
                    partner={partner}
                    assignableRoles={assignableRoles}
                />
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                        <Users className="h-4 w-4" /> Staff (
                        {listQuery.data?.length ?? 0})
                    </CardTitle>
                    <CardDescription>
                        Assignable roles for a partneradmin:{' '}
                        {assignableRoles.map((r) => (
                            <Badge
                                key={r}
                                variant="outline"
                                className="ml-1 font-mono"
                            >
                                {r}
                            </Badge>
                        ))}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {listQuery.isLoading ? (
                        <div className="space-y-2">
                            <Skeleton className="h-10 w-full" />
                            <Skeleton className="h-10 w-full" />
                            <Skeleton className="h-10 w-full" />
                        </div>
                    ) : listQuery.error ? (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Failed to load team</AlertTitle>
                            <AlertDescription>
                                {listQuery.error.message}
                            </AlertDescription>
                        </Alert>
                    ) : !listQuery.data || listQuery.data.length === 0 ? (
                        <div className="py-8 text-center text-sm text-muted-foreground">
                            No staff users currently scoped to{' '}
                            <span className="font-mono">{partner}</span>.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>User</TableHead>
                                    <TableHead>Current roles</TableHead>
                                    {assignableRoles.map((r) => (
                                        <TableHead
                                            key={r}
                                            className="text-center"
                                        >
                                            {r}
                                        </TableHead>
                                    ))}
                                    <TableHead className="w-[1%]" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {listQuery.data.map((u) => (
                                    <TeamRow
                                        key={u.clerkUserId}
                                        user={u}
                                        assignableRoles={assignableRoles}
                                        partner={partner}
                                        isSaving={
                                            setRolesMutation.isPending &&
                                            setRolesMutation.variables
                                                ?.clerkUserId === u.clerkUserId
                                        }
                                        onSave={async (nextAssigned) => {
                                            try {
                                                await setRolesMutation.mutateAsync(
                                                    {
                                                        partner,
                                                        clerkUserId:
                                                            u.clerkUserId,
                                                        roles: nextAssigned,
                                                    },
                                                );
                                                toast.success(
                                                    `Updated roles for ${u.email ?? u.clerkUserId}`,
                                                );
                                            } catch (err) {
                                                toast.error(
                                                    err instanceof Error
                                                        ? err.message
                                                        : 'Failed to update roles',
                                                );
                                            }
                                        }}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

interface TeamUser {
    clerkUserId: string;
    email: string | null;
    name: string | null;
    tgdRoles: string[];
    whitelabelOwnership: string | null;
}

/**
 * One row = one team member. Local `pending` state tracks checkbox
 * changes before the user clicks Save; the mutation replaces only
 * the assignable slice of tgdRoles (preserved roles stay as-is
 * server-side).
 */
function TeamRow({
    user,
    assignableRoles,
    partner,
    isSaving,
    onSave,
}: {
    user: TeamUser;
    assignableRoles: readonly string[];
    partner: string;
    isSaving: boolean;
    onSave: (nextAssigned: string[]) => void | Promise<void>;
}) {
    const currentAssigned = React.useMemo(
        () => user.tgdRoles.filter((r) => assignableRoles.includes(r)),
        [user.tgdRoles, assignableRoles],
    );
    const preservedRoles = React.useMemo(
        () => user.tgdRoles.filter((r) => !assignableRoles.includes(r)),
        [user.tgdRoles, assignableRoles],
    );

    const [pending, setPending] = React.useState<string[]>(currentAssigned);

    React.useEffect(() => {
        setPending(currentAssigned);
    }, [currentAssigned]);

    const dirty = React.useMemo(() => {
        const a = [...currentAssigned].sort().join(',');
        const b = [...pending].sort().join(',');
        return a !== b;
    }, [currentAssigned, pending]);

    const toggleRole = (role: string) => {
        setPending((prev) =>
            prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
        );
    };

    const scopeMismatch = user.whitelabelOwnership !== partner;

    return (
        <TableRow>
            <TableCell>
                <div className="flex flex-col">
                    <span className="font-medium">
                        {user.name ?? user.email ?? user.clerkUserId}
                    </span>
                    {user.email && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Mail className="h-3 w-3" /> {user.email}
                        </span>
                    )}
                    <span className="font-mono text-[10px] text-muted-foreground">
                        {user.clerkUserId}
                    </span>
                </div>
            </TableCell>
            <TableCell>
                <div className="flex flex-wrap gap-1">
                    {user.tgdRoles.length === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                        user.tgdRoles.map((r) => (
                            <Badge
                                key={r}
                                variant={
                                    assignableRoles.includes(r)
                                        ? 'default'
                                        : 'secondary'
                                }
                                className="font-mono text-[10px]"
                            >
                                {r}
                            </Badge>
                        ))
                    )}
                    {preservedRoles.length > 0 && (
                        <span
                            title="These roles are superadmin-only and preserved on save."
                            className="text-[10px] text-muted-foreground"
                        >
                            (locked)
                        </span>
                    )}
                </div>
            </TableCell>
            {assignableRoles.map((r) => (
                <TableCell key={r} className="text-center">
                    <Checkbox
                        checked={pending.includes(r)}
                        disabled={isSaving || scopeMismatch}
                        onCheckedChange={() => toggleRole(r)}
                        aria-label={`Toggle ${r}`}
                    />
                </TableCell>
            ))}
            <TableCell>
                <Button
                    size="sm"
                    disabled={!dirty || isSaving || scopeMismatch}
                    onClick={() => onSave(pending)}
                >
                    {isSaving ? (
                        <>
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" /> Saving
                        </>
                    ) : (
                        'Save'
                    )}
                </Button>
            </TableCell>
        </TableRow>
    );
}

/**
 * Dialog to onboard a teammate by email.
 *
 * Server enforces the security rules (see `invitePartnerStaff`); this UI
 * just collects email + roles and surfaces the resulting status as a toast.
 * Outcomes:
 *   - role_updated: the email was already on this partner, roles merged.
 *   - invited:      no Clerk account existed, invitation email sent.
 *   - error toast:  any cross-partner / internal-staff / hijack refusal.
 */
function InviteTeamMemberDialog({
    partner,
    assignableRoles,
}: {
    partner: string;
    assignableRoles: readonly string[];
}) {
    const [open, setOpen] = React.useState(false);
    const [email, setEmail] = React.useState('');
    const [selectedRoles, setSelectedRoles] = React.useState<string[]>([]);

    const utils = trpc.useUtils();
    const inviteMutation = trpc.partners.inviteTeamMember.useMutation({
        onSuccess: async (result) => {
            await utils.partners.listTeam.invalidate();
            if (result.status === 'invited') {
                toast.success(
                    `Invitation sent to ${result.email}. They'll join the team after accepting.`,
                );
            } else {
                toast.success('Roles updated for existing team member.');
            }
            setOpen(false);
            setEmail('');
            setSelectedRoles([]);
        },
        onError: (err) => {
            toast.error(err.message);
        },
    });

    // Default the role checkbox to the first assignable role on first open
    // so the form is submittable in one click for the common case.
    React.useEffect(() => {
        if (open && selectedRoles.length === 0 && assignableRoles.length > 0) {
            setSelectedRoles([assignableRoles[0]!]);
        }
    }, [open, assignableRoles, selectedRoles.length]);

    const toggleRole = (role: string) => {
        setSelectedRoles((prev) =>
            prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
        );
    };

    const canSubmit =
        email.length > 0 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
        selectedRoles.length > 0 &&
        !inviteMutation.isPending;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        inviteMutation.mutate({
            partner,
            email: email.trim(),
            roles: selectedRoles,
        });
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm">
                    <UserPlus className="mr-2 h-4 w-4" />
                    Invite member
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle>Invite a teammate</DialogTitle>
                        <DialogDescription>
                            Add someone to{' '}
                            <span className="font-mono">{partner}</span>'s
                            staff. If they don't have a Clerk account yet,
                            we'll send them an invitation email; otherwise
                            we'll merge the requested roles onto their
                            existing account.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="invite-email">Email</Label>
                            <Input
                                id="invite-email"
                                type="email"
                                placeholder="teammate@company.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={inviteMutation.isPending}
                                autoFocus
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Roles</Label>
                            <div className="space-y-2">
                                {assignableRoles.map((r) => (
                                    <label
                                        key={r}
                                        className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted/40"
                                    >
                                        <Checkbox
                                            checked={selectedRoles.includes(r)}
                                            onCheckedChange={() => toggleRole(r)}
                                            disabled={inviteMutation.isPending}
                                        />
                                        <span className="font-mono">{r}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <Alert>
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription className="text-xs">
                                If the email is already linked to a Clerk
                                account outside this partner (other partner,
                                tagada staff, or unrelated user), the request
                                will be rejected. Ask a tagada superadmin in
                                that case.
                            </AlertDescription>
                        </Alert>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setOpen(false)}
                            disabled={inviteMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!canSubmit}>
                            {inviteMutation.isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                    Inviting
                                </>
                            ) : (
                                'Send invite'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
